import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { nomenclatorV2Api, MappingRulePayload } from '../../services/nomenclatorV2Api'

interface MappingRule {
  id: string
  target_type: string
  target_value: string
  dicom_field: string
  pattern_type: string
  pattern: string
  priority: number
  confidence_score?: number
  modality_filter?: string
  source?: string
  description?: string
}

const TARGET_TYPES = [
  { value: 'region', label: 'Regiune' },
  { value: 'subregion', label: 'Subregiune' },
  { value: 'technique', label: 'Tehnică' },
  { value: 'laterality', label: 'Lateralitate' },
  { value: 'purpose', label: 'Purpose' },
  { value: 'segment', label: 'Segment' }
]

const DICOM_FIELDS = [
  { value: 'StudyDescription', label: 'Study Description' },
  { value: 'SeriesDescription', label: 'Series Description' },
  { value: 'ProtocolName', label: 'Protocol Name' },
  { value: 'BodyPartExamined', label: 'Body Part Examined' }
]

const PATTERN_TYPES = [
  { value: 'regex', label: 'Regex' },
  { value: 'keyword', label: 'Keyword' },
  { value: 'exact', label: 'Exact' }
]

const MODALITIES = ['CT', 'MR', 'DX', 'US', 'NM', 'PT', 'MG']

export const MappingRulesRefactored: React.FC = () => {
  const [rules, setRules] = useState<MappingRule[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null)
  const [testText, setTestText] = useState('')
  const [testResult, setTestResult] = useState<{ match: boolean; error?: string } | null>(null)

  const [form, setForm] = useState<MappingRulePayload>({
    target_type: 'region',
    target_value: '',
    dicom_field: 'StudyDescription',
    pattern_type: 'regex',
    pattern: '',
    priority: 100
  })

  const loadRules = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await nomenclatorV2Api.listMappingRules()
      setRules(data)
    } catch (err) {
      console.error(err)
      setError('Nu am putut încărca regulile de mapping')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRules()
  }, [])

  const handleSubmit = async () => {
    if (!form.target_value || !form.pattern) {
      setError('Valoare țintă și Pattern sunt obligatorii')
      return
    }

    try {
      if (editingId) {
        await nomenclatorV2Api.updateMappingRule(editingId, form)
        setSuccess('Regulă actualizată cu succes')
      } else {
        await nomenclatorV2Api.createMappingRule(form)
        setSuccess('Regulă adăugată cu succes')
      }
      resetForm()
      loadRules()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la salvare')
    }
  }

  const handleEdit = (rule: MappingRule) => {
    setForm({
      target_type: rule.target_type,
      target_value: rule.target_value,
      dicom_field: rule.dicom_field,
      pattern_type: rule.pattern_type,
      pattern: rule.pattern,
      priority: rule.priority,
      confidence_score: rule.confidence_score,
      modality_filter: rule.modality_filter,
      source: rule.source,
      description: rule.description
    })
    setEditingId(rule.id)
  }

  const handleDeleteClick = (id: string) => {
    setRuleToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) return

    try {
      await nomenclatorV2Api.deleteMappingRule(ruleToDelete)
      setSuccess('Regulă ștearsă cu succes')
      loadRules()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la ștergere')
    } finally {
      setDeleteDialogOpen(false)
      setRuleToDelete(null)
    }
  }

  const resetForm = () => {
    setForm({
      target_type: 'region',
      target_value: '',
      dicom_field: 'StudyDescription',
      pattern_type: 'regex',
      pattern: '',
      priority: 100
    })
    setEditingId(null)
    setTestText('')
    setTestResult(null)
  }

  const testPattern = () => {
    if (!form.pattern || !testText) {
      setTestResult({ match: false, error: 'Completează pattern și textul de test' })
      return
    }

    try {
      let match = false
      if (form.pattern_type === 'regex') {
        const regex = new RegExp(form.pattern, 'i')
        match = regex.test(testText)
      } else if (form.pattern_type === 'exact') {
        match = testText.trim().toUpperCase() === form.pattern.trim().toUpperCase()
      } else if (form.pattern_type === 'keyword') {
        match = testText.toLowerCase().includes(form.pattern.toLowerCase())
      }
      setTestResult({ match })
    } catch (err: any) {
      setTestResult({ match: false, error: `Eroare regex: ${err.message}` })
    }
  }

  if (loading && rules.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  // Compact styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' }
  }
  const tableSx = {
    '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' },
    '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 },
    '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.78rem' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess(null)} message={success} />

      {/* Test Playground - Compact */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>Test Pattern</Typography>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="flex-start">
            <TextField
              label="Text de test (ex: 'TAP +C', 'CT Creier Nativ')"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 200, ...inputSx }}
              placeholder="Introdu text DICOM..."
            />
            <TextField
              label="Pattern"
              value={form.pattern}
              onChange={(e) => setForm({ ...form, pattern: e.target.value })}
              size="small"
              sx={{ flex: 1, minWidth: 200, ...inputSx }}
              placeholder="Pattern regex/keyword/exact..."
            />
            <Button variant="outlined" onClick={testPattern} disabled={!form.pattern || !testText} size="small" sx={{ height: 32, fontSize: '0.75rem', textTransform: 'none' }}>
              Testează
            </Button>
          </Box>
          {testResult && (
            <Box sx={{ mt: 1 }}>
              {testResult.error ? (
                <Alert severity="error" sx={{ py: 0.5, fontSize: '0.78rem' }}>{testResult.error}</Alert>
              ) : (
                <Alert severity={testResult.match ? 'success' : 'warning'} sx={{ py: 0.5, fontSize: '0.78rem', bgcolor: testResult.match ? 'success.dark' : 'warning.dark', color: testResult.match ? 'success.contrastText' : 'warning.contrastText' }}>
                  <Chip label={testResult.match ? 'MATCH' : 'NO MATCH'} color={testResult.match ? 'success' : 'warning'} size="small" sx={{ mr: 1, height: 18, fontSize: '0.68rem' }} />
                  {testResult.match ? 'Pattern-ul se potrivește!' : 'Pattern-ul NU se potrivește.'}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Form - Compact */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AddIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {editingId ? 'Editează Regulă' : 'Adaugă Regulă Nouă'}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" gap={1} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 110, ...inputSx }}>
                <InputLabel>Tip Țintă</InputLabel>
                <Select value={form.target_type} onChange={(e) => setForm({ ...form, target_type: e.target.value })} label="Tip Țintă">
                  {TARGET_TYPES.map((type) => (<MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.8rem' }}>{type.label}</MenuItem>))}
                </Select>
              </FormControl>
              <TextField label="Valoare Țintă" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} required size="small" sx={{ minWidth: 120, ...inputSx }} />
              <FormControl size="small" sx={{ minWidth: 140, ...inputSx }}>
                <InputLabel>Câmp DICOM</InputLabel>
                <Select value={form.dicom_field} onChange={(e) => setForm({ ...form, dicom_field: e.target.value })} label="Câmp DICOM">
                  {DICOM_FIELDS.map((field) => (<MenuItem key={field.value} value={field.value} sx={{ fontSize: '0.8rem' }}>{field.label}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100, ...inputSx }}>
                <InputLabel>Tip Pattern</InputLabel>
                <Select value={form.pattern_type} onChange={(e) => setForm({ ...form, pattern_type: e.target.value })} label="Tip Pattern">
                  {PATTERN_TYPES.map((type) => (<MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.8rem' }}>{type.label}</MenuItem>))}
                </Select>
              </FormControl>
              <TextField label="Pattern" value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} required size="small" sx={{ flex: 1, minWidth: 150, ...inputSx }} />
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <TextField label="Prioritate" type="number" value={form.priority ?? 100} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} size="small" sx={{ width: 90, ...inputSx }} />
              <TextField label="Confidence" type="number" value={form.confidence_score ?? ''} onChange={(e) => setForm({ ...form, confidence_score: e.target.value ? Number(e.target.value) : undefined })} size="small" inputProps={{ step: 0.01, min: 0, max: 1 }} sx={{ width: 100, ...inputSx }} />
              <FormControl size="small" sx={{ minWidth: 120, ...inputSx }}>
                <InputLabel>Filtru Modalitate</InputLabel>
                <Select value={form.modality_filter || ''} onChange={(e) => setForm({ ...form, modality_filter: e.target.value || undefined })} label="Filtru Modalitate">
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}>-</MenuItem>
                  {MODALITIES.map((mod) => (<MenuItem key={mod} value={mod} sx={{ fontSize: '0.8rem' }}>{mod}</MenuItem>))}
                </Select>
              </FormControl>
              <TextField label="Descriere" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} size="small" sx={{ flex: 1, minWidth: 180, ...inputSx }} />
              <Button variant="contained" onClick={handleSubmit} disabled={!form.target_value || !form.pattern} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>
                {editingId ? 'Salvează' : 'Adaugă'}
              </Button>
              {editingId && <Button variant="outlined" onClick={resetForm} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Anulează</Button>}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Table - Compact */}
      <Card sx={tableSx}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
            Lista Reguli ({rules.length})
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 } }}>
                <TableCell>Tip Țintă</TableCell>
                <TableCell>Valoare Țintă</TableCell>
                <TableCell>Câmp DICOM</TableCell>
                <TableCell>Tip Pattern</TableCell>
                <TableCell>Pattern</TableCell>
                <TableCell align="right">Prioritate</TableCell>
                <TableCell align="right">Confidence</TableCell>
                <TableCell>Filtru Modalitate</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.id} hover sx={{ bgcolor: editingId === rule.id ? 'action.hover' : 'inherit' }}>
                  <TableCell>{TARGET_TYPES.find((t) => t.value === rule.target_type)?.label || rule.target_type}</TableCell>
                  <TableCell><strong>{rule.target_value}</strong></TableCell>
                  <TableCell>{DICOM_FIELDS.find((f) => f.value === rule.dicom_field)?.label || rule.dicom_field}</TableCell>
                  <TableCell>{PATTERN_TYPES.find((t) => t.value === rule.pattern_type)?.label || rule.pattern_type}</TableCell>
                  <TableCell><code style={{ fontSize: '0.75em' }}>{rule.pattern}</code></TableCell>
                  <TableCell align="right">{rule.priority}</TableCell>
                  <TableCell align="right">{rule.confidence_score ? rule.confidence_score.toFixed(2) : '-'}</TableCell>
                  <TableCell>{rule.modality_filter || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(rule)} color="primary"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(rule.id)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Dialog - Compact */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>Confirmare ștergere</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.85rem' }}>Sigur vrei să ștergi regula de mapping <strong>{ruleToDelete}</strong>?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>Anulează</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>Șterge</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

